import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUniversity, faComments, faGlobe, faHandshake, faLink, faHome } from '@fortawesome/free-solid-svg-icons';

const GlobalNav = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle scroll effect for sticky nav
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Function to handle logo click
  const handleLogoClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMenuOpen(false); // Close mobile menu if open
    
    // If we're already on home page, just scroll to top
    if (location.pathname === '/') {
      window.scrollTo({ 
        top: 0, 
        behavior: 'smooth' 
      });
    } else {
      // Navigate to home page
      navigate('/');
    }
  };

  // Function to toggle mobile menu
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Function to handle mobile link click
  const handleMobileLinkClick = () => {
    setIsMenuOpen(false); // Close menu after clicking a link
  };

  return (
    <header 
      className={`global-nav ${isScrolled ? 'scrolled' : ''} ${isMenuOpen ? 'menu-open' : ''}`}
      style={{
        position: 'sticky',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        transition: 'all 0.3s ease-in-out'
      }}
    >
      <div className="container">
        <div className="nav-content">
          {/* Logo Section - Entire area clickable */}
          <div 
            className="nav-logo-area"
            onClick={handleLogoClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleLogoClick(e);
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Go to homepage"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              cursor: 'pointer',
              textDecoration: 'none',
              padding: '8px 12px',
              borderRadius: '8px',
              transition: 'all 0.3s ease',
              position: 'relative'
            }}
          >
            {/* Web3 Logo Image with Home Icon Overlay */}
            <div style={{ position: 'relative' }}>
              <img 
                src="/web3.png" 
                alt="Web3 Logo - Click to go to homepage" 
                style={{ 
                  height: '45px',
                  width: 'auto',
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 2px 4px rgba(255,215,0,0.4))',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.filter = 'drop-shadow(0 4px 12px rgba(255,215,0,0.7))';
                  e.currentTarget.style.transform = 'scale(1.08) rotate(2deg)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.filter = 'drop-shadow(0 2px 4px rgba(255,215,0,0.4))';
                  e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
                }}
              />
              {/* Home icon overlay that appears on hover */}
              <div 
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  opacity: 0,
                  transition: 'all 0.3s ease',
                  color: 'var(--gold)',
                  textShadow: '0 0 10px rgba(255,215,0,0.8)',
                  pointerEvents: 'none',
                  zIndex: 2
                }}
                className="home-icon-overlay"
              >
                <FontAwesomeIcon icon={faHome} size="lg" />
              </div>
            </div>

            {/* Web3Reg Logo Text */}
            <div 
              style={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '2px'
              }}
            >
              <div 
                className="gold-logo"
                style={{ 
                  fontSize: '1.5rem', 
                  cursor: 'pointer',
                  textShadow: '0 2px 4px rgba(255,215,0,0.4)',
                  transition: 'all 0.3s ease',
                  userSelect: 'none',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.textShadow = '0 4px 12px rgba(255,215,0,0.7)';
                  e.currentTarget.style.color = '#FFE44D';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.textShadow = '0 2px 4px rgba(255,215,0,0.4)';
                  e.currentTarget.style.color = '';
                }}
              >
                Web3Reg
                {/* Home indicator line that appears on hover */}
                <div 
                  style={{
                    position: 'absolute',
                    bottom: '-5px',
                    left: 0,
                    width: '0%',
                    height: '2px',
                    background: 'var(--gold)',
                    transition: 'width 0.3s ease',
                    boxShadow: '0 0 8px var(--gold)'
                  }}
                  className="home-indicator"
                />
              </div>
              <div 
                style={{ 
                  fontSize: '0.75rem', 
                  color: 'var(--text-secondary)',
                  opacity: 0.7,
                  transition: 'all 0.3s ease'
                }}
                className="nav-tagline"
              >
                Decentralized Web3 Portal
              </div>
            </div>

            {/* Hover effect for entire logo area */}
            <style jsx="true">{`
              .nav-logo-area:hover .home-icon-overlay {
                opacity: 0.9 !important;
                transform: translate(-50%, -50%) scale(1.2) !important;
              }
              .nav-logo-area:hover .home-indicator {
                width: 100% !important;
              }
              .nav-logo-area:hover .nav-tagline {
                opacity: 1 !important;
                color: var(--gold) !important;
              }
            `}</style>
          </div>

          {/* Desktop Navigation Links */}
          <nav className={`nav-links ${isMenuOpen ? 'active' : ''}`}>
            <a 
              href="https://banksystem.co" 
              target="_blank" 
              rel="noopener noreferrer"
              className="nav-link"
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              onClick={handleMobileLinkClick}
            >
              <FontAwesomeIcon icon={faUniversity} />
              <span>BankSystem</span>
            </a>
            <a 
              href="https://zpost.ai" 
              target="_blank" 
              rel="noopener noreferrer"
              className="nav-link"
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              onClick={handleMobileLinkClick}
            >
              <FontAwesomeIcon icon={faComments} />
              <span>Zpost</span>
            </a>
            <a 
              href="https://www.namecheap.com/domains/registration/results/?domain=yourname.myurl&hns=true" 
              target="_blank" 
              rel="noopener noreferrer"
              className="nav-link"
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              onClick={handleMobileLinkClick}
            >
              <FontAwesomeIcon icon={faGlobe} />
              <span>MyUrl</span>
            </a>
            <a 
              href="https://handshake.org" 
              target="_blank" 
              rel="noopener noreferrer"
              className="nav-link"
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              onClick={handleMobileLinkClick}
            >
              <FontAwesomeIcon icon={faHandshake} />
              <span>Handshake</span>
            </a>
            <a 
              href="https://learn.namebase.io/starting-from-zero/how-to-access-handshake-sites"
              target="_blank" 
              rel="noopener noreferrer"
              className="nav-link"
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              onClick={handleMobileLinkClick}
            >
              <FontAwesomeIcon icon={faLink} />
              <span>Resolve</span>
            </a>
          </nav>

          {/* Mobile Menu Toggle Button */}
          <button 
            className="nav-toggle"
            onClick={toggleMenu}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            style={{
              display: 'none',
              background: 'none',
              border: 'none',
              color: 'var(--gold)',
              fontSize: '1.5rem',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '4px',
              transition: 'all 0.3s ease'
            }}
          >
            <div style={{
              width: '24px',
              height: '2px',
              background: 'currentColor',
              position: 'relative',
              transition: 'all 0.3s ease'
            }}>
              <span style={{
                position: 'absolute',
                width: '100%',
                height: '2px',
                background: 'currentColor',
                top: isMenuOpen ? '0' : '-6px',
                left: '0',
                transform: isMenuOpen ? 'rotate(45deg)' : 'none',
                transition: 'all 0.3s ease'
              }} />
              <span style={{
                position: 'absolute',
                width: '100%',
                height: '2px',
                background: 'currentColor',
                top: isMenuOpen ? '0' : '6px',
                left: '0',
                transform: isMenuOpen ? 'rotate(-45deg)' : 'none',
                transition: 'all 0.3s ease'
              }} />
            </div>
          </button>
        </div>
      </div>

      {/* Inline CSS for sticky nav and hover effects */}
      <style jsx="true">{`
        .global-nav {
          background: linear-gradient(to bottom, rgba(0, 0, 0, 0.98) 0%, rgba(0, 0, 0, 0.95) 100%);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid transparent;
          transition: all 0.3s ease-in-out;
        }
        
        .global-nav.scrolled {
          background: rgba(0, 0, 0, 0.98) !important;
          border-bottom: 1px solid var(--gold) !important;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5) !important;
        }
        
        .global-nav.menu-open {
          background: rgba(0, 0, 0, 0.99) !important;
        }
        
        .nav-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 0;
          max-width: 1400px;
          margin: 0 auto;
        }
        
        .nav-links {
          display: flex;
          gap: 1.5rem;
          align-items: center;
        }
        
        .nav-link {
          color: var(--text-light);
          text-decoration: none;
          padding: 0.5rem 1rem;
          border-radius: 6px;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          border: 1px solid transparent;
        }
        
        .nav-link:hover {
          color: var(--gold) !important;
          background: rgba(255, 215, 0, 0.1) !important;
          border-color: var(--gold) !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 4px 12px var(--gold-shadow) !important;
        }
        
        /* Mobile responsive styles */
        @media (max-width: 768px) {
          .nav-toggle {
            display: block !important;
          }
          
          .nav-links {
            position: fixed;
            top: 70px;
            left: 0;
            right: 0;
            background: rgba(0, 0, 0, 0.98);
            backdrop-filter: blur(20px);
            flex-direction: column;
            padding: 1rem;
            gap: 1rem;
            border-bottom: 2px solid var(--gold);
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
            transform: translateY(-100%);
            opacity: 0;
            visibility: hidden;
            transition: all 0.3s ease;
            z-index: 999;
          }
          
          .nav-links.active {
            transform: translateY(0) !important;
            opacity: 1 !important;
            visibility: visible !important;
          }
          
          .nav-link {
            width: 100%;
            text-align: center;
            padding: 1rem;
            justify-content: center;
          }
        }
        
        @media (max-width: 480px) {
          .nav-logo-area {
            padding: 6px !important;
          }
          
          .gold-logo {
            font-size: 1.3rem !important;
          }
          
          .nav-tagline {
            display: none;
          }
        }
      `}</style>
    </header>
  );
};

export default GlobalNav;
